import { apiInstance } from '@/helpers/axios'
import type { AxiosRequestConfig } from 'axios'

// Base API configuration

/**
 * Fetch collection data from the API
 * @param endpoint API endpoint for the collection
 * @param params Query parameters for filtering, sorting, pagination
 * @param requestConfig Optional extra axios config (e.g. `{ meta: { silentError: true } }`
 *   to suppress the global error dialog for this request).
 * @returns Promise with collection data
 */
export async function fetchCollectionData(
	endpoint: string,
	params: Record<string, any> = {},
	requestConfig?: Partial<AxiosRequestConfig>
) {
	try {
		const apiClient = apiInstance
		const response = await apiClient.get(`${endpoint}`, {
			params,
			...requestConfig,
		})
		return response.data
	} catch (error) {
		console.error(`Error fetching data from ${endpoint}:`, error)
		throw error
	}
}

/**
 * Create a new item in a collection
 * @param endpoint API endpoint for the collection
 * @param data Item data to create
 * @returns Promise with created item
 */
export async function createCollectionItem(endpoint: string, data: any) {
	try {
		const apiClient = apiInstance
		const response = await apiClient.post(`${endpoint}`, data)
		return response.data
	} catch (error) {
		console.error(`Error creating item in ${endpoint}:`, error)
		throw error
	}
}

/**
 * Update an existing item in a collection
 * @param endpoint API endpoint for the collection
 * @param id Item ID to update
 * @param data Updated item data
 * @returns Promise with updated item
 */
export async function updateCollectionItem(
	endpoint: string,
	id: string | number,
	data: any
) {
	try {
		const apiClient = apiInstance
		const response = await apiClient.put(`${endpoint}/${id}`, data)
		return response.data
	} catch (error) {
		console.error(`Error updating item ${id} in ${endpoint}:`, error)
		throw error
	}
}

/**
 * Delete an item from a collection
 * @param endpoint API endpoint for the collection
 * @param id Item ID to delete
 * @returns Promise with deletion result
 */
export async function deleteCollectionItem(
	endpoint: string,
	id: string | number
) {
	try {
		const apiClient = apiInstance
		const response = await apiClient.delete(`${endpoint}/${id}`)
		return response.data
	} catch (error) {
		console.error(`Error deleting item ${id} from ${endpoint}:`, error)
		throw error
	}
}
